/** Shared Palletways order-detail enrichment helpers (Orders + Profit Compute). */

export const AUTO_ENRICH_BATCH_SIZE = 10;
/** Resume the next enrich batch when each gated stage is at or below this (waiting + in-flight). */
export const PIPELINE_SAFE_DEPTH = 30;
export const PIPELINE_GATE_POLL_MS = 12_000;
export const RAW_IMPORT_QUEUE_NAMES = ['raw-import-requested', 'raw-object-stored'] as const;
export const EVENT_COMMIT_QUEUE_NAMES = ['observed-event-committed'] as const;
/** v2 keys by Palletways tracking ID (stable). v1 used entity ids and restarted after remount. */
export const ENRICH_STORAGE_KEY = 'zecure.orders.lazyEnrich.v2';
export const ENRICH_STORAGE_KEY_LEGACY = 'zecure.orders.lazyEnrich.v1';
export const ENRICH_ATTEMPTED_CAP = 3000;

export type EnrichPlace = {
  name?: string;
  address?: string;
  postcode?: string;
};

export type EnrichableOrder = {
  id: string;
  name?: string;
  status?: string;
  source?: string;
  trackingId?: string;
  customerRef?: string;
  pickup?: EnrichPlace;
  delivery?: EnrichPlace;
  weightKg?: number;
  type?: string;
  lifts?: number;
};

export type EnrichPersist = {
  /** Tracking IDs already tried this session (success or hard fail — not 429). */
  attempted: string[];
  /** Tracking IDs still known incomplete when we last saved (resume order after HMR). */
  pending: string[];
  used: number;
  budget: number;
};

/** Town/place for display — never include postcode. */
export function placeNameForTable(place?: EnrichPlace): string {
  const name = place?.name?.trim();
  if (name) return name;
  const addr = place?.address?.trim();
  if (!addr) return '—';
  const postcode = place?.postcode?.trim();
  if (postcode) {
    const escaped = postcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const stripped = addr
      .replace(new RegExp(`,\\s*${escaped}\\s*$`, 'i'), '')
      .replace(new RegExp(`\\s+${escaped}\\s*$`, 'i'), '')
      .trim();
    if (stripped) return stripped;
  }
  return addr;
}

export function orderNeedsLazyPlaceEnrichment(order: EnrichableOrder): boolean {
  if (order.source !== 'palletways') return false;
  if (!String(order.trackingId || '').trim()) return false;
  const originMissing = placeNameForTable(order.pickup) === '—';
  const destMissing = placeNameForTable(order.delivery) === '—';
  const customerMissing = !String(order.customerRef || '').trim();
  return originMissing || destMissing || customerMissing;
}

export function enrichTrackingKey(order: EnrichableOrder): string {
  return String(order.trackingId || '').trim();
}

/** Pause after each batch even if sessionStorage still holds a larger leftover budget. */
export function clampEnrichSessionBudget(used: number, storedBudget: number): number {
  const batch = AUTO_ENRICH_BATCH_SIZE;
  const nextStop =
    used > 0 && used % batch === 0
      ? used
      : Math.floor(used / batch) * batch + batch;
  if (!Number.isFinite(storedBudget)) return nextStop;
  return Math.min(Math.max(storedBudget, 0), nextStop);
}

export function loadEnrichPersist(): EnrichPersist {
  const empty: EnrichPersist = {
    attempted: [],
    pending: [],
    used: 0,
    budget: AUTO_ENRICH_BATCH_SIZE,
  };
  try {
    const rawV2 = sessionStorage.getItem(ENRICH_STORAGE_KEY);
    if (rawV2) {
      const parsed = JSON.parse(rawV2);
      const used = Number.isFinite(parsed?.used) ? Math.max(0, Number(parsed.used)) : 0;
      return {
        attempted: Array.isArray(parsed?.attempted) ? parsed.attempted.map(String) : [],
        pending: Array.isArray(parsed?.pending) ? parsed.pending.map(String) : [],
        used,
        budget: clampEnrichSessionBudget(
          used,
          Number.isFinite(parsed?.budget) ? Number(parsed.budget) : AUTO_ENRICH_BATCH_SIZE,
        ),
      };
    }
    // Migrate budget/used from v1; drop entity-id attempted list (wrong key space).
    const rawV1 = sessionStorage.getItem(ENRICH_STORAGE_KEY_LEGACY);
    if (rawV1) {
      const parsed = JSON.parse(rawV1);
      const used = Number.isFinite(parsed?.used) ? Math.max(0, Number(parsed.used)) : 0;
      return {
        ...empty,
        used,
        budget: clampEnrichSessionBudget(
          used,
          Number.isFinite(parsed?.budget) ? Number(parsed.budget) : AUTO_ENRICH_BATCH_SIZE,
        ),
      };
    }
    return empty;
  } catch {
    return empty;
  }
}

export function saveEnrichPersist(
  attempted: Set<string>,
  pending: string[],
  used: number,
  budget: number,
) {
  try {
    const attemptedList = Array.from(attempted);
    const trimmed =
      attemptedList.length > ENRICH_ATTEMPTED_CAP
        ? attemptedList.slice(attemptedList.length - ENRICH_ATTEMPTED_CAP)
        : attemptedList;
    sessionStorage.setItem(
      ENRICH_STORAGE_KEY,
      JSON.stringify({
        attempted: trimmed,
        pending: pending.slice(0, ENRICH_ATTEMPTED_CAP),
        used,
        budget,
      }),
    );
  } catch {
    /* ignore quota */
  }
}

export type PipelineGateDepths = {
  rawImport: number;
  eventCommit: number;
};

/** Waiting + in-flight for Raw Import and Event Commit (Data Import pipeline). */
export function sumPipelineQueueDepths(
  queues: Record<string, { visible?: number; notVisible?: number }>,
  names: readonly string[],
): number {
  let total = 0;
  for (const name of names) {
    const q = queues[name];
    if (!q) continue;
    total += Number(q.visible ?? 0) + Number(q.notVisible ?? 0);
  }
  return total;
}

/** Map fleet-intelligence orders/list row → EnrichableOrder (same shape as FleetDataHub). */
export function mapOrdersListApiRow(o: Record<string, unknown>): EnrichableOrder {
  const sourceRaw = String(o.source || 'unknown');
  const isPalletways = sourceRaw === 'palletways';
  const formatPlace = (town?: unknown, postcode?: unknown) =>
    [town, postcode].map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  const originLabel = formatPlace(o.originTown, o.originPostcode);
  const destinationLabel = formatPlace(o.destinationTown, o.destinationPostcode);
  return {
    id: String(o.entityId || `${sourceRaw}:${o.externalRef || Math.random()}`),
    name: String(o.externalRef || o.entityId || ''),
    status: String(o.status || ''),
    source: sourceRaw === 'declared' ? 'same_day' : sourceRaw,
    trackingId: isPalletways ? String(o.trackingId || '') : undefined,
    customerRef: String(o.customerReference || ''),
    pickup: (originLabel || o.originTown)
      ? {
          name: o.originTown ? String(o.originTown) : undefined,
          address: originLabel || undefined,
          postcode: o.originPostcode ? String(o.originPostcode) : undefined,
        }
      : undefined,
    delivery: (destinationLabel || o.destinationTown || o.deliveryCompany)
      ? {
          name: String(o.destinationTown || o.deliveryCompany || '') || undefined,
          address: destinationLabel || (o.deliveryCompany ? String(o.deliveryCompany) : undefined),
          postcode: o.destinationPostcode ? String(o.destinationPostcode) : undefined,
        }
      : undefined,
    weightKg: typeof o.weightKg === 'number' ? o.weightKg : undefined,
    type: o.type ? String(o.type) : undefined,
    lifts: typeof o.lifts === 'number' ? o.lifts : undefined,
  };
}

/** Apply consignment-status API body onto a matching order row. */
export function applyConsignmentStatusPatch<T extends EnrichableOrder>(
  row: T,
  order: EnrichableOrder,
  trackingId: string,
  body: Record<string, unknown>,
): T {
  if (!((row.trackingId && row.trackingId === trackingId) || row.id === order.id)) {
    return row;
  }
  const formatPlace = (town?: unknown, postcode?: unknown) =>
    [town, postcode].map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  const nextStatus = String(body?.palletwaysStatus || '').trim();
  const originLabel = formatPlace(body?.originTown, body?.originPostcode);
  const destinationLabel = formatPlace(body?.destinationTown, body?.destinationPostcode);
  const next: T = { ...row };
  if (nextStatus) next.status = nextStatus;
  if (body?.consignmentNo) next.name = String(body.consignmentNo);
  if (body?.customer) next.customerRef = String(body.customer);
  if (typeof body?.weightKg === 'number') next.weightKg = body.weightKg as number;
  if (body?.type) next.type = String(body.type);
  if (typeof body?.lifts === 'number') next.lifts = body.lifts as number;
  if (originLabel || body?.originTown || body?.originName) {
    next.pickup = {
      ...next.pickup,
      name: String(body?.originTown || body?.originName || next.pickup?.name || '') || undefined,
      address: originLabel || String(body?.originName || next.pickup?.address || '') || undefined,
      postcode: body?.originPostcode
        ? String(body.originPostcode)
        : next.pickup?.postcode,
    };
  }
  if (destinationLabel || body?.destinationTown || body?.destinationName || body?.deliveryCompany) {
    next.delivery = {
      ...next.delivery,
      name:
        String(
          body?.destinationTown || body?.destinationName || body?.deliveryCompany || next.delivery?.name || '',
        ) || undefined,
      address:
        destinationLabel
        || String(body?.deliveryCompany || next.delivery?.address || '')
        || undefined,
      postcode: body?.destinationPostcode
        ? String(body.destinationPostcode)
        : next.delivery?.postcode,
    };
  }
  return next;
}
